import { useEffect, useState, useRef, useCallback, useMemo } from "react"
import styled from "styled-components"
import LockOutlined from "@mui/icons-material/LockOutlined"
import PageContent from "~components/PageContent"
import useProofsOfOwnership from "~hooks/useProofsOfOwnership"
import useAssignedProofsOfOwnership from "~hooks/useAssignedProofsOfOwnership"
import useOperatorLabel from "~hooks/useOperatorLabel"
import useProofsOfOwnershipCounts from "~hooks/useProofsOfOwnershipCounts"
import useSecurities from "~hooks/useSecurities"
import { NoTableData } from "~styled/table"
import { toast } from "react-toastify"
import { PooPayload, ProofOfOwnership } from "~shapes/proofOfOwnership"
import { PROOF_OF_OWNERSHIP_STATUS } from "~constants/proofOfOwnership"
import { useAuthContext } from "~context/authContext"
import {
  isAdminOperator,
  isPooAssignedToMe,
  POO_ASSIGNED_VIEW,
  pooAssignmentErrorMessage,
  PooView,
} from "~utils/pooAssignment"
import { asApiError } from "~utils/apiError"
import { useMutation, useQueryClient } from "react-query"
import { FormProvider, useForm } from "react-hook-form"
import moment from "moment/moment"
import { getLastDayOfMonth, getPooFileId, getPooUserId } from "~utils/utils"
import { buildPortfolioPayload, buildSecurityLabel } from "~utils/portfolio"
import { buildCorrectionsPayload } from "~utils/pooCorrections"
import { getAiSuggestionHint } from "~utils/aiSuggestion"
import InvalidatePooModal from "~components/portfolio/InvalidatePooModal"
import ConfirmationModal from "~components/ConfirmationModal"
import { PooInitialState, userInitialState } from "../../../../initialStates"
import ProofOfOwnershipService from "~services/ProofOfOwnershipService"
import PortfolioService from "~services/PortfolioService"
import Portfolio from "~components/portfolio/Portfolio"
import { PortfolioPayload } from "~shapes/portfolio"
import ValidationActions from "~components/portfolio/ValidationActions"
import { useUser } from "~hooks/useUser"
import { UserShape } from "~shapes/user"
import ProofOfOwnershipViewer from "~components/proofOfOwnership/ProofOfOwnershipViewer"
import ProofOfOwnershipHeader from "~components/proofOfOwnership/ProofOfOwnershipHeader"
import AISuggestionBox from "~components/proofOfOwnership/AISuggestionBox"
import AIValidationSuggestionBox from "~components/proofOfOwnership/AIValidationSuggestionBox"
import AINotProcessedBox from "~components/proofOfOwnership/AINotProcessedBox"
import AIReviewReasonNote from "~components/proofOfOwnership/AIReviewReasonNote"
import AIAuthenticityConfirmBox from "~components/proofOfOwnership/AIAuthenticityConfirmBox"
import AISuggestionNote from "~components/proofOfOwnership/AISuggestionNote"
import { AISuggestionResponse } from "~shapes/proofOfOwnership"
import colors from "~constants/colors"

export interface SearchOptionShape {
  email?: string
}

const DEFAULT_SORT = "created_at,asc"

const updateUrlParameters = (
  email?: string,
  status?: string,
  sort?: string,
) => {
  const queryParams = new URLSearchParams(window.location.search)
  if (email) {
    queryParams.set("email", email)
  } else {
    queryParams.delete("email")
  }

  if (status) {
    queryParams.set("status", status)
  } else {
    queryParams.delete("status")
  }

  if (sort && sort !== DEFAULT_SORT) {
    queryParams.set("sort", sort)
  } else {
    queryParams.delete("sort")
  }

  const newUrl = `${window.location.pathname}?${queryParams.toString()}`
  window.history.replaceState(null, "", newUrl)
}

const getUrlParameters = (): {
  email: string | null
  status: PooView | null
  pooId: string | null
  sort: string
} => {
  const queryParams = new URLSearchParams(window.location.search)
  const email = decodeURIComponent(queryParams.get("email") || "")
  const status = queryParams.get("status") as PooView | null
  const pooId = queryParams.get("pooId")
  const sort = queryParams.get("sort") || DEFAULT_SORT
  return { email, status, pooId, sort }
}

// Stable signature of the form fields the AI suggestion controls (name, broker,
// month, year, holdings). Used to tell whether the operator has edited away from
// the suggested values: if the current signature differs from the snapshot taken
// when the suggestion was applied, the "accept" button is disabled; reverting the
// edits brings the signature back and re-enables it. Holdings are normalized
// (zeros/blanks dropped, key-sorted) so order and empty rows don't register as
// edits.
const serializeSuggestionRelevant = (values: any): string => {
  const portfolio =
    values?.portfolio && typeof values.portfolio === "object"
      ? values.portfolio
      : {}
  const normalizedHoldings = Object.entries(portfolio)
    .map(([code, qty]) => [code, Number(qty) || 0] as [string, number])
    .filter(([, qty]) => qty !== 0)
    .sort(([a], [b]) => a.localeCompare(b))

  return JSON.stringify({
    name: String(values?.name ?? ""),
    broker: String(values?.broker ?? ""),
    month: Number(values?.month) || null,
    year: Number(values?.year) || null,
    holdings: normalizedHoldings,
  })
}

const CreatePortfolio = () => {
  // Basic state
  const [selectedStatement, setSelectedStatement] =
    useState<ProofOfOwnership>(PooInitialState)
  const [isInvalidationModalOpened, setIsInvalidationModalOpened] =
    useState<boolean>(false)
  // Replaces the native confirm() for irreversible actions. `action` runs on
  // confirm; `isConfirming` drives the modal's loading state.
  const [confirmModal, setConfirmModal] = useState<{
    message: string
    title?: string
    confirmLabel?: string
    action: () => void | Promise<void>
  } | null>(null)
  const [isConfirming, setIsConfirming] = useState<boolean>(false)
  const [reason, setReason] = useState<string>("")
  const [isSaving, setIsSaving] = useState<boolean>(false)
  const [aiSuggestion, setAiSuggestion] = useState<AISuggestionResponse | null>(
    null,
  )
  const [isLoadingAiSuggestion, setIsLoadingAiSuggestion] =
    useState<boolean>(false)
  const [aiEnhancedPortfolio, setAiEnhancedPortfolio] = useState<any[]>([])
  const [aiFilledFields, setAiFilledFields] = useState<Set<string>>(new Set())
  const [isProcessingAiSuggestion, setIsProcessingAiSuggestion] =
    useState<boolean>(false)
  // True once the operator edits any field away from the applied AI suggestion;
  // disables the accept button until the values are reverted. Null baseline means
  // there's no active suggestion to compare against (or it hasn't settled yet).
  const [isSuggestionDirty, setIsSuggestionDirty] = useState<boolean>(false)
  const suggestionBaselineRef = useRef<string | null>(null)

  // URL parameters
  const { email, status, sort: initialSort } = getUrlParameters()
  const [user, setUser] = useState<UserShape>(userInitialState)
  const [pooFilterStatus, setPooFilterStatus] = useState<PooView | null>(
    status || PROOF_OF_OWNERSHIP_STATUS.CREATED,
  )
  const [sort, setSort] = useState<string>(initialSort)
  const [searchOption, setSearchOption] = useState<SearchOptionShape>({
    email: email || undefined,
  })
  const [isSearchingUser, setIsSearchingUser] = useState<boolean>(!!email)

  // Current operator + role-derived capabilities
  const { loggedUser } = useAuthContext()
  const isAssignedView = pooFilterStatus === POO_ASSIGNED_VIEW
  // The operator validating the selected POO (email / SYSTEM name), resolved
  // from its assignee. Used in the header badge, read-only banner and the
  // take-over confirmation.
  const resolvedAssigneeLabel = useOperatorLabel(selectedStatement.assignee)

  // Refs for cleanup
  const isMountedRef = useRef(true)
  const abortControllerRef = useRef<AbortController | null>(null)
  const lastFetchedFileIdRef = useRef<string | null>(null)
  // POOs claimed/taken-over in this session. The assignee id the POO service
  // returns isn't guaranteed to equal our JWT-derived id, so right after a claim
  // we can't always recognise the POO as "ours" by comparison — track ids here
  // so the verdict actions stay enabled.
  const claimedByMeRef = useRef<Set<string>>(new Set())
  // Guards the irreversible confirm action against a double trigger (e.g. the
  // window Enter shortcut firing alongside a focused confirm button).
  const confirmingRef = useRef(false)

  // Form
  const form = useForm()
  const queryClient = useQueryClient()

  // User hook
  const {
    fetchUser,
    loading: isFetchingUser,
    error: userError,
  } = useUser(setUser)

  // Securities query with 5-minute caching to avoid rate limiting
  const { data: securitiesList = [] } = useSecurities()

  // Status-filtered list (Created / Validating). Disabled while the assigned
  // queue is active so only one list fetches at a time.
  const regularPooQuery = useProofsOfOwnership(
    searchOption.email ? user?.id : "",
    isAssignedView
      ? undefined
      : (pooFilterStatus as PROOF_OF_OWNERSHIP_STATUS | null) || undefined,
    !isAssignedView && (!searchOption.email || !!user?.id),
    sort,
    // Always refetch on mount so a POO created on another screen shows up the
    // moment the operator lands back on this validation queue.
    "always",
  )

  // Operator validation queue (GET .../assigned) — always the caller's own
  // queue. Active only on the "My validations" pill.
  const assignedPooQuery = useAssignedProofsOfOwnership(
    {},
    isAssignedView,
    sort,
    "always",
  )

  // Lightweight own-queue fetch kept alive to drive the "My validations" pill
  // count even while another pill is active.
  const ownAssignedQueue = useAssignedProofsOfOwnership({}, true)

  // Single active list the rest of the screen consumes, regardless of source.
  const {
    isFetching: isFetchingPoo,
    data: pooList,
    refetch: fetchPooList,
    isError: isErrorFetchingPooList,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = isAssignedView ? assignedPooQuery : regularPooQuery

  // Per-state POO counts (e.g. { created: 0, validating: 1 }) shown on the filter pills
  const { data: pooCounts, isFetching: isFetchingPooCounts } =
    useProofsOfOwnershipCounts(
      searchOption.email ? user?.id : "",
      !searchOption.email || !!user?.id,
    )

  // Enhanced list that includes the current item even if it doesn't match the filter
  const displayList = useMemo(() => {
    if (!pooList) return []

    // Only add items to the list when viewing CREATED state to keep transitioning items visible
    if (
      pooFilterStatus === PROOF_OF_OWNERSHIP_STATUS.CREATED &&
      selectedStatement &&
      selectedStatement.id &&
      selectedStatement.id !== PooInitialState.id
    ) {
      const isInList = pooList.some(item => item.id === selectedStatement.id)
      if (!isInList) {
        return [selectedStatement, ...pooList]
      }
    }

    return pooList
  }, [pooList, selectedStatement, pooFilterStatus])

  // Mutations
  const { mutateAsync: mutatePortfolioAsync } = useMutation(
    (payload: PortfolioPayload) => PortfolioService.postUserPortfolio(payload),
    {
      retry: 2,
      onError: error => {
        console.error("Portfolio creation failed:", error)
      },
    },
  )

  const { mutateAsync: mutatePooAsync } = useMutation((payload: PooPayload) => {
    return ProofOfOwnershipService.patchProofOfOwnership(
      payload.state,
      payload.documentId,
      payload.invalidation_reason || undefined,
      payload.name || undefined,
      payload.broker || undefined,
      payload.date || undefined,
      payload.person_names || undefined,
    )
  })

  const { mutateAsync: getAiSuggestionAsync } = useMutation(
    (fileId: string) => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
      abortControllerRef.current = new AbortController()
      return ProofOfOwnershipService.getProofOfOwnershipData(fileId)
    },
    {
      onSuccess: (data: AISuggestionResponse) => {
        if (isMountedRef.current && data) {
          // Hold the suggestion so the header can show the analyzer version for
          // any POO whose data endpoint returns something. Pre-filling the form
          // is the caller's job (validation flow only) — see openClaimedPoo /
          // loadPooData.
          setAiSuggestion(data)
        }
      },
      onError: (error: any) => {
        if (isMountedRef.current) {
          if (error?.message === "NO_AI_DATA") {
            setAiSuggestion(null)
          } else {
            setAiSuggestion(null)
            setAiFilledFields(new Set())
          }
        }
      },
    },
  )

  // Helper functions
  const applyPortfolioFromAi = useCallback(
    (shares: Record<string, number>) => {
      if (!isMountedRef.current || securitiesList.length === 0) return

      const matchedPortfolio: Record<string, number> = {}
      const aiEnhancedPortfolioData: any[] = []

      Object.entries(shares).forEach(([securityCode, quantity]) => {
        const matchedSecurity = securitiesList.find(
          security => security.code === securityCode,
        )

        if (matchedSecurity && quantity > 0) {
          matchedPortfolio[matchedSecurity.code] =
            (matchedPortfolio[matchedSecurity.code] || 0) + quantity
        }
      })

      Object.entries(matchedPortfolio).forEach(([security, quantity]) => {
        aiEnhancedPortfolioData.push({ security, quantity })
      })

      if (Object.keys(matchedPortfolio).length > 0) {
        form.setValue("portfolio", matchedPortfolio)
        setAiEnhancedPortfolio(aiEnhancedPortfolioData)

        setAiFilledFields(prev => {
          const portfolioFields = Object.keys(matchedPortfolio)
          const newSet = new Set(Array.from(prev))
          portfolioFields.forEach(field => newSet.add(field))
          return newSet
        })
      }
    },
    [securitiesList.length, form],
  )

  const applyAiSuggestionToForm = useCallback(
    (data: AISuggestionResponse) => {
      if (!isMountedRef.current || !data) return

      const filledFields = new Set<string>()

      if (data.brokerage_name) {
        form.setValue("broker", data.brokerage_name)
        filledFields.add("broker")
      }
      if (data.person_name) {
        form.setValue("name", data.person_name)
        filledFields.add("name")
      }
      if (data.document_date) {
        const date = moment(data.document_date)
        form.setValue("month", date.month() + 1)
        form.setValue("year", date.year())
        filledFields.add("month")
        filledFields.add("year")
      }

      setAiFilledFields(filledFields)

      // Apply portfolio data directly here instead of calling applyPortfolioFromAi
      if (data.shares && securitiesList.length > 0) {
        const matchedPortfolio: Record<string, number> = {}
        const aiEnhancedPortfolioData: any[] = []

        Object.entries(data.shares).forEach(([securityCode, quantity]) => {
          const matchedSecurity = securitiesList.find(
            security => security.code === securityCode,
          )

          if (matchedSecurity && quantity > 0) {
            matchedPortfolio[matchedSecurity.code] =
              (matchedPortfolio[matchedSecurity.code] || 0) + quantity
          }
        })

        Object.entries(matchedPortfolio).forEach(([security, quantity]) => {
          aiEnhancedPortfolioData.push({ security, quantity })
        })

        if (Object.keys(matchedPortfolio).length > 0) {
          form.setValue("portfolio", matchedPortfolio)
          setAiEnhancedPortfolio(aiEnhancedPortfolioData)

          const portfolioFields = Object.keys(matchedPortfolio)
          setAiFilledFields(prev => {
            const newSet = new Set(Array.from(prev))
            portfolioFields.forEach(field => newSet.add(field))
            return newSet
          })
        }
      }
    },
    [securitiesList.length, form],
  )

  // Keeps every list and the pill counts in sync after a claim/verdict/release.
  const invalidatePooLists = useCallback(() => {
    queryClient.invalidateQueries(["proofs-of-ownership-counts"])
    // Mark every cached POO list stale — including the user-profile POO tab,
    // which uses this same query keyed on status "all". refetchActive:false skips
    // the active list (callers refetch it explicitly) so we don't replay its
    // already-loaded pages.
    queryClient.invalidateQueries(["user-proofs-of-ownership"], {
      refetchActive: false,
    })
    queryClient.invalidateQueries(["assigned-proofs-of-ownership"])
  }, [queryClient])

  // Open a freshly-claimed (VALIDATING) POO: select it, refresh lists, and load
  // its AI suggestion. Shared by the normal claim and the admin take-over.
  const openClaimedPoo = useCallback(
    async (claimed: ProofOfOwnership) => {
      // Remember we own this one this session so the verdict flow stays enabled
      // even if the assignee id doesn't equal our JWT id (see claimedByMeRef).
      claimedByMeRef.current.add(claimed.id)
      setSelectedStatement(claimed)
      invalidatePooLists()

      const pooFileId = getPooFileId(claimed)
      if (pooFileId) {
        lastFetchedFileIdRef.current = pooFileId
        setIsLoadingAiSuggestion(true)
        try {
          // Pre-fill the form with the extracted data for BOTH recommendations.
          // On reject the backend still returns person_name/broker/date/shares,
          // and surfacing them lets the operator override-and-edit from the
          // suggestion — which keeps the accepted-vs-edited tracking meaningful.
          const data = await getAiSuggestionAsync(pooFileId)
          if (data) applyAiSuggestionToForm(data)
        } catch {
          // handled by mutation onError
        } finally {
          if (isMountedRef.current) setIsLoadingAiSuggestion(false)
        }
      }
    },
    [invalidatePooLists, getAiSuggestionAsync, applyAiSuggestionToForm],
  )

  // Event handlers
  // Claim the selected POO for validation: POST .../assignment self-assigns the
  // caller and moves CREATED -> VALIDATING atomically. On 409 the POO is already
  // being validated by someone else; refreshing surfaces the current assignee.
  const startValidation = useCallback(async () => {
    const target = selectedStatement
    try {
      const claimed = await ProofOfOwnershipService.claimProofOfOwnership(
        target.id,
      )
      await openClaimedPoo(claimed)
    } catch (error) {
      // A 409 means someone else won the claim race — the POO is no longer in
      // Created. Refresh and drop the stale selection; otherwise displayList's
      // "keep the transitioning item visible" logic re-adds it to the Created
      // list. Clearing it also lets auto-select move to the next available POO.
      if (asApiError(error)?.status === 409) {
        await fetchPooList()
        if (isMountedRef.current) {
          setAiSuggestion(null)
          setAiFilledFields(new Set())
          setSelectedStatement(PooInitialState)
        }
      }
      toast.error(
        pooAssignmentErrorMessage(error, "Failed to start validation"),
      )
    }
  }, [selectedStatement, openClaimedPoo, fetchPooList])

  // Admin override: take over a POO already claimed by another operator. The
  // backend reassigns the claim to the requesting admin (ROLE_ADMIN only).
  const executeTakeOver = useCallback(async () => {
    try {
      const claimed = await ProofOfOwnershipService.claimProofOfOwnership(
        selectedStatement.id,
        { override: true },
      )
      if (isMountedRef.current) {
        toast.success("You've taken over this validation")
        await openClaimedPoo(claimed)
      }
    } catch (error) {
      if (isMountedRef.current) {
        toast.error(
          pooAssignmentErrorMessage(
            error,
            "Failed to take over this validation",
          ),
        )
      }
    }
  }, [selectedStatement.id, openClaimedPoo])

  const handleTakeOver = useCallback(() => {
    const who = resolvedAssigneeLabel ?? "another operator"
    setConfirmModal({
      title: "Take over validation?",
      message: `Heads up: ${who} is currently working on this validation right now — they may be gathering information, reviewing the document or about to submit a verdict. Taking it over reassigns the validation to you and removes their access, so any work in progress could be lost. Only do this if you're sure they're no longer on it. Continue?`,
      confirmLabel: "Take over",
      action: executeTakeOver,
    })
  }, [resolvedAssigneeLabel, executeTakeOver])

  // Release ("Soltar") a claim: DELETE .../assignment moves the POO
  // VALIDATING -> CREATED and clears the assignee. Reversible and emits no
  // notification, so it runs without a confirmation step.
  const executeRelease = useCallback(async () => {
    try {
      await ProofOfOwnershipService.releaseProofOfOwnership(
        selectedStatement.id,
      )
      if (isMountedRef.current) {
        toast.success("Validation released")
        await fetchPooList()
        invalidatePooLists()
        setAiSuggestion(null)
        setAiFilledFields(new Set())
        setSelectedStatement(PooInitialState)
      }
    } catch (error) {
      if (isMountedRef.current) {
        toast.error(
          pooAssignmentErrorMessage(
            error,
            "Failed to release. You may not own this claim.",
          ),
        )
      }
    }
  }, [selectedStatement.id, fetchPooList, invalidatePooLists])

  const handleRelease = useCallback(() => executeRelease(), [executeRelease])

  // The suggestion loads AFTER selectedStatement changes, so callbacks keyed
  // only on selectedStatement would capture a null aiSuggestion. Read both from
  // refs that always track the latest value so the validate/accept/invalidate
  // callbacks (and sendCorrections) never go stale.
  const selectedStatementRef = useRef(selectedStatement)
  const aiSuggestionRef = useRef(aiSuggestion)
  useEffect(() => {
    selectedStatementRef.current = selectedStatement
  }, [selectedStatement])
  useEffect(() => {
    aiSuggestionRef.current = aiSuggestion
  }, [aiSuggestion])

  const savePoo = useCallback(
    async (formData: any) => {
      const date = moment({
        year: formData.year,
        month: formData.month - 1,
        day: getLastDayOfMonth(formData.month, formData.year),
      }).format("YYYY-MM-DDTHH:mm:ss[Z]")

      const payload: PooPayload = {
        documentId: selectedStatement.id,
        state: PROOF_OF_OWNERSHIP_STATUS.VALIDATED,
        name: formData.name,
        // Forwarded from the AI suggestion as-is (not surfaced in the UI), so the
        // full holder list persists on the POO alongside the primary `name`.
        person_names: aiSuggestionRef.current?.person_names,
        broker: formData.broker,
        date: date,
      }

      const response = await mutatePooAsync(payload)
      setSelectedStatement(response)
    },
    [selectedStatement.id, mutatePooAsync],
  )

  // Records the operator's final values (the "truth") against the AI suggestion
  // so the server can compute accepted-vs-edited. Sent on validate and
  // invalidate (NOT discard), even when nothing was edited — an untouched accept
  // is a valid "accepted: true" data point. Best-effort: it must never block or
  // fail the operator's action, and it only runs when there was a suggestion to
  // compare against.
  const sendCorrections = useCallback(
    async (
      state: "VALIDATED" | "INVALIDATED",
      formData: any,
      invalidationReason: string,
    ) => {
      const fileId = getPooFileId(selectedStatementRef.current)
      const aiSuggestion = aiSuggestionRef.current
      if (!fileId || !aiSuggestion) return

      try {
        const payload = buildCorrectionsPayload(
          state,
          formData,
          invalidationReason,
          aiSuggestion,
        )
        await ProofOfOwnershipService.postProofOfOwnershipDataCorrections(
          fileId,
          payload,
        )
      } catch (error) {
        console.error("Failed to record POO corrections:", error)
      }
    },
    [],
  )

  const savePortfolio = useCallback(
    async (formData: any) => {
      const date = moment({
        year: formData.year,
        month: formData.month - 1,
        day: getLastDayOfMonth(formData.month, formData.year),
      }).format("YYYY-MM-DDTHH:mm:ss[Z]")

      const requestBody = buildPortfolioPayload(formData, securitiesList, {
        userId: getPooUserId(selectedStatement),
        fallbackBroker: selectedStatement.broker,
        pooId: selectedStatement.id,
        date,
      })

      // No positive holdings → don't create a portfolio (all-zero / empty form).
      if (!requestBody) return false

      await mutatePortfolioAsync(requestBody)
      return true
    },
    [
      selectedStatement.broker,
      selectedStatement.id,
      mutatePortfolioAsync,
      securitiesList,
    ],
  )

  const executeSaveAndValidate = useCallback(
    async (formData: any) => {
      setIsSaving(true)
      try {
        await savePortfolio(formData)
        try {
          await savePoo(formData)
          await sendCorrections("VALIDATED", formData, "NONE")
          if (isMountedRef.current) {
            await fetchPooList()
            invalidatePooLists()
            setAiSuggestion(null)
            setAiFilledFields(new Set())
            setSelectedStatement(PooInitialState)
          }
        } catch (pooError) {
          if (isMountedRef.current) {
            toast.warning(
              pooAssignmentErrorMessage(
                pooError,
                "The portfolio was created, but the proof of ownership could NOT be validated.",
              ),
            )
          }
        }
      } catch (portfolioError) {
        if (isMountedRef.current) {
          toast.error("Error while saving the portfolio. Please try again.")
        }
      } finally {
        if (isMountedRef.current) {
          setIsSaving(false)
        }
      }
    },
    [savePortfolio, savePoo, sendCorrections, fetchPooList, invalidatePooLists],
  )

  const handleSavePortfolioAndValidatePoo = useCallback(
    async (formData: any) => {
      setConfirmModal({
        title: "Create portfolio & validate",
        message:
          "This action can't be reverted. Are you sure you want to create this portfolio and validate the proof of ownership?",
        confirmLabel: "Create & Validate",
        action: () => executeSaveAndValidate(formData),
      })
    },
    [executeSaveAndValidate],
  )

  const executeDiscard = useCallback(async () => {
    const payload: PooPayload = {
      documentId: selectedStatement.id,
      state: PROOF_OF_OWNERSHIP_STATUS.DISCARDED,
    }

    try {
      await mutatePooAsync(payload)
      if (isMountedRef.current) {
        await fetchPooList()
        invalidatePooLists()
        setAiSuggestion(null)
        setAiFilledFields(new Set())
        setSelectedStatement(PooInitialState)
      }
    } catch (error) {
      if (isMountedRef.current) {
        toast.error(
          pooAssignmentErrorMessage(
            error,
            `There was an error saving the portfolio. Try again.`,
          ),
        )
        setIsSaving(false)
      }
    }
  }, [selectedStatement.id, mutatePooAsync, fetchPooList, invalidatePooLists])

  const handleDiscard = useCallback(() => {
    setConfirmModal({
      title: "Discard proof of ownership",
      message:
        "This action can't be reverted, are you sure you want to discard this proof of ownership? It won't send any notification.",
      confirmLabel: "Discard",
      action: executeDiscard,
    })
  }, [executeDiscard])

  const runConfirmedAction = useCallback(async () => {
    if (!confirmModal || confirmingRef.current) return
    confirmingRef.current = true
    setIsConfirming(true)
    try {
      await confirmModal.action()
    } finally {
      confirmingRef.current = false
      if (isMountedRef.current) {
        setIsConfirming(false)
        setConfirmModal(null)
      }
    }
  }, [confirmModal])

  const executeInvalidation = useCallback(
    async (reasonToApply: string) => {
      setIsSaving(true)
      try {
        await mutatePooAsync({
          documentId: selectedStatement.id,
          state: PROOF_OF_OWNERSHIP_STATUS.INVALIDATED,
          invalidation_reason: reasonToApply,
          person_names: aiSuggestionRef.current?.person_names,
        })
        await sendCorrections("INVALIDATED", form.getValues(), reasonToApply)

        if (isMountedRef.current) {
          toast.success(`Portfolio invalidated`)
          await fetchPooList()
          invalidatePooLists()
          setAiSuggestion(null)
          setAiFilledFields(new Set())
          setSelectedStatement(PooInitialState)
          setIsInvalidationModalOpened(false)
        }
      } catch (error) {
        if (isMountedRef.current) {
          toast.error(
            pooAssignmentErrorMessage(
              error,
              `There was an error invalidating the portfolio. Try again.`,
            ),
          )
        }
      } finally {
        if (isMountedRef.current) {
          setIsSaving(false)
        }
      }
    },
    [
      selectedStatement.id,
      mutatePooAsync,
      sendCorrections,
      form,
      fetchPooList,
      invalidatePooLists,
    ],
  )

  const handleSubmitModal = useCallback(
    () => executeInvalidation(reason),
    [executeInvalidation, reason],
  )

  const handleSortChange = useCallback((newSort: string) => {
    setSort(newSort)
    setSelectedStatement(PooInitialState)
  }, [])

  const onSearch = useCallback(
    (el: any) => {
      updateUrlParameters(el.email, pooFilterStatus || undefined, sort)
      setSearchOption(el)
    },
    [pooFilterStatus, sort],
  )

  const handleValidation = useCallback(() => {
    form.trigger().then(() => {
      form.handleSubmit(handleSavePortfolioAndValidatePoo)()
    })
  }, [form, handleSavePortfolioAndValidatePoo])

  const handleAcceptAiSuggestion = useCallback(async () => {
    if (!isMountedRef.current || !aiSuggestion) return

    // No one-click accept for a not-processed document — there's no suggestion
    // to act on, only a blank form to fill. (The "a" shortcut is gated too.)
    const hint = getAiSuggestionHint(aiSuggestion)
    if (!hint || hint.kind === "not_processed") return
    const suggestsReject = hint.kind === "reject"

    setIsProcessingAiSuggestion(true)
    try {
      if (suggestsReject) {
        // Re-open the reason-picker modal as a reconfirmation step instead of
        // invalidating directly. The modal pre-selects the AI's suggested reason
        // (via suggestedReason), so the operator can confirm or change it before
        // the POO is rejected.
        setIsInvalidationModalOpened(true)
      } else {
        // Suggests accept: populate the form with the AI data, then open the
        // validate confirmation.
        if (aiSuggestion.brokerage_name) {
          form.setValue("broker", aiSuggestion.brokerage_name)
        }
        if (aiSuggestion.person_name) {
          form.setValue("name", aiSuggestion.person_name)
        }
        if (aiSuggestion.document_date) {
          const date = moment(aiSuggestion.document_date)
          form.setValue("month", date.month() + 1)
          form.setValue("year", date.year())
        }
        if (aiSuggestion.shares) {
          applyPortfolioFromAi(aiSuggestion.shares)
        }

        await form.trigger()
        await handleSavePortfolioAndValidatePoo(form.getValues())
      }
    } catch (error) {
      if (isMountedRef.current) {
        toast.error("Error processing recommendation. Please try again.")
      }
    } finally {
      if (isMountedRef.current) {
        setIsProcessingAiSuggestion(false)
      }
    }
  }, [
    aiSuggestion,
    form,
    handleSavePortfolioAndValidatePoo,
    selectedStatement.id,
    mutatePooAsync,
    fetchPooList,
    applyPortfolioFromAi,
  ])

  // Simplified effects
  useEffect(() => {
    if (userError && isMountedRef.current) {
      toast.error(`Failed to find user`)
      setSelectedStatement(PooInitialState)
      queryClient.setQueriesData(["user-proofs-of-ownership"], {
        pages: [],
        pageParams: [],
      })
      setIsSearchingUser(false)
    }
  }, [userError])

  // Mount/cleanup (must run first so isMountedRef is set before other effects in Strict Mode)
  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
    }
  }, [])

  // Handle user search
  useEffect(() => {
    if (!isMountedRef.current) return

    setIsSearchingUser(true)
    setSelectedStatement(PooInitialState)

    const searchUser = async () => {
      try {
        if (searchOption.email) {
          await fetchUser({ email: searchOption.email })
        } else {
          setUser(userInitialState)
        }
      } catch (error) {
        console.error("Error fetching user:", error)
        setUser(userInitialState)
      } finally {
        if (isMountedRef.current) {
          setIsSearchingUser(false)
        }
      }
    }

    searchUser()
  }, [searchOption.email])

  // Sync URL with filter state
  useEffect(() => {
    if (!isMountedRef.current) return
    updateUrlParameters(searchOption.email, pooFilterStatus || undefined, sort)
  }, [pooFilterStatus, searchOption.email, sort])

  // Reset selected statement when filter changes (before new list loads)
  useEffect(() => {
    if (!isMountedRef.current) return
    setSelectedStatement(PooInitialState)
    // The list refetches on a pill switch (its query key changes), but the
    // counts query key is stable, so its badge would keep showing a stale value.
    // Refresh it so the pill count matches the freshly-loaded list.
    queryClient.invalidateQueries(["proofs-of-ownership-counts"])
  }, [pooFilterStatus, queryClient])

  // Auto-select first item when list loads (e.g. after filter switch)
  useEffect(() => {
    if (!isMountedRef.current || !displayList || displayList.length === 0) {
      return
    }

    const hasNoSelection = selectedStatement.id === PooInitialState.id
    const selectedNotInList = !displayList.some(
      item => item.id === selectedStatement.id,
    )

    if (hasNoSelection || selectedNotInList) {
      setSelectedStatement(displayList[0])
    }
  }, [displayList, pooFilterStatus])

  // Handle selected statement changes - load user, AI, reset form once per POO selection
  useEffect(() => {
    if (
      !isMountedRef.current ||
      !selectedStatement ||
      selectedStatement.id === "" ||
      selectedStatement.id === PooInitialState.id
    )
      return

    const pooUserId = getPooUserId(selectedStatement)
    const pooFileId = getPooFileId(selectedStatement)
    const isValidating =
      selectedStatement.state === PROOF_OF_OWNERSHIP_STATUS.VALIDATING

    const loadPooData = async () => {
      setAiSuggestion(null)
      setAiFilledFields(new Set())
      setAiEnhancedPortfolio([])
      setIsLoadingAiSuggestion(false)
      suggestionBaselineRef.current = null
      setIsSuggestionDirty(false)
      lastFetchedFileIdRef.current = null
      form.reset()
      // Seed the form store (not just the DOM defaultValue) so month/year
      // survive the reset above and are authoritative at save/validate time.
      // Defaults to the current month/year; a VALIDATED AI suggestion with a
      // document_date overrides these via applyAiSuggestionToForm below.
      form.setValue("month", moment().month() + 1)
      form.setValue("year", moment().year())

      if (pooUserId) {
        await fetchUser({ id: pooUserId })
      }

      // Load the suggestion for ANY selected POO (not just while validating) so
      // the header can surface the analyzer version whenever the data endpoint
      // returns something. The form is only pre-filled while validating — for
      // other states it isn't rendered.
      if (pooFileId) {
        lastFetchedFileIdRef.current = pooFileId
        setIsLoadingAiSuggestion(true)
        try {
          const data = await getAiSuggestionAsync(pooFileId)
          if (data && isValidating) applyAiSuggestionToForm(data)
        } catch (err) {
          // Error handled by getAiSuggestionAsync / UI state
        } finally {
          if (isMountedRef.current) setIsLoadingAiSuggestion(false)
        }
      }
    }

    loadPooData()
  }, [selectedStatement?.id])

  // Handle POO list errors
  useEffect(() => {
    if (isErrorFetchingPooList) {
      toast.error(`Error getting the proof of ownership`)
    }
  }, [isErrorFetchingPooList])

  // Snapshot the form once the suggestion has been applied — this is the
  // "matches the suggestion" baseline the accept button is gated on. Keyed on
  // both aiSuggestion and aiEnhancedPortfolio so it runs after the holdings have
  // been seeded into the form store (child effects commit before this one).
  useEffect(() => {
    if (aiSuggestion) {
      suggestionBaselineRef.current = serializeSuggestionRelevant(
        form.getValues(),
      )
      setIsSuggestionDirty(false)
    } else {
      suggestionBaselineRef.current = null
      setIsSuggestionDirty(false)
    }
  }, [aiSuggestion, aiEnhancedPortfolio, form])

  // Re-evaluate dirtiness on every field change. The seeding setValue calls fire
  // before the baseline is captured (so they're skipped via the null guard);
  // after that, only operator edits flip the flag — and reverting the edits
  // brings the signature back to the baseline and clears it.
  useEffect(() => {
    const subscription = form.watch(values => {
      const baseline = suggestionBaselineRef.current
      if (!baseline) return
      setIsSuggestionDirty(serializeSuggestionRelevant(values) !== baseline)
    })
    return () => subscription.unsubscribe()
  }, [form])

  const isValidating =
    selectedStatement.state === PROOF_OF_OWNERSHIP_STATUS.VALIDATING
  const isCreated =
    selectedStatement.state === PROOF_OF_OWNERSHIP_STATUS.CREATED
  const hasSelection = selectedStatement.id !== PooInitialState.id
  // Don't treat a next-page fetch as a full loading state — only the initial fetch should block the UI
  const isInitialFetching = isFetchingPoo && !isFetchingNextPage

  // When an email search is active but the user hasn't resolved yet (or wasn't found),
  // the POO query key falls back to the "all POOs" key and returns stale cached data.
  // Suppress that stale list so only the searched user's POOs are ever shown.
  // The assigned queue isn't keyed by POO owner, so the email gate never applies there.
  const hasActiveSearch = !isAssignedView && !!searchOption.email
  const isWaitingForUser = hasActiveSearch && !user?.id
  const effectiveLoading =
    isInitialFetching || (!isAssignedView && isSearchingUser)
  const effectiveList = isWaitingForUser ? [] : displayList

  // Whether the selected POO belongs to the current operator: its own queue (My
  // validations), one we claimed/took-over this session, or an id match. An
  // unassigned (legacy) POO is editable by anyone but isn't "owned".
  const ownedByMe =
    isAssignedView ||
    claimedByMeRef.current.has(selectedStatement.id) ||
    isPooAssignedToMe(selectedStatement, loggedUser)

  // A POO actively being validated by someone else: everything is locked and the
  // only way in is an admin "Take over".
  const isForeignValidation =
    isValidating && !!selectedStatement.assignee && !ownedByMe

  const canRelease = isValidating && ownedByMe && !!selectedStatement.assignee
  const canTakeOver = isForeignValidation && isAdminOperator(loggedUser)

  // Editable when it's yours or an unassigned legacy POO. Foreign POOs are fully
  // read-only — an admin must Take over first (the Validate path would otherwise
  // create the portfolio before the backend rejects the verdict).
  const canFinalize = ownedByMe || !selectedStatement.assignee

  const showContentArea = !effectiveLoading && hasSelection
  // The form (statement details) shows for any in-progress POO so the operator
  // can see the extracted data; it's rendered read-only when the POO is foreign.
  const showForm = showContentArea && isValidating && !!user?.id
  const isReadOnlyValidation = showContentArea && isForeignValidation
  // Surface the assignee (header badge + read-only banner) only when it's
  // someone else's POO.
  const selectedAssigneeLabel = isForeignValidation
    ? resolvedAssigneeLabel
    : null
  const showEmptyState =
    !effectiveLoading && (isErrorFetchingPooList || effectiveList.length === 0)

  // The hint we paint over the suggestion: not_processed (yellow, no data) /
  // reject (red) / validate (green). See getAiSuggestionHint for precedence.
  const aiHint = getAiSuggestionHint(aiSuggestion)
  const aiSuggestsReject = aiHint?.kind === "reject"
  // Missing-field review and authenticity-gate reasons both highlight the
  // prefilled fields amber instead of green: needs_input because there's
  // nothing to accept yet, confirm_authenticity because the operator must
  // read the warning before accepting (still one-click, just flagged).
  const aiSuggestsReview =
    aiHint?.kind === "needs_input" || aiHint?.kind === "confirm_authenticity"
  // A not-processed document has nothing to one-click accept (no suggested data).
  const aiSuggestionActionable =
    aiHint?.kind === "reject" ||
    aiHint?.kind === "validate" ||
    aiHint?.kind === "confirm_authenticity"

  // Pre-filled holdings surfaced in the validate banner (code → readable label,
  // e.g. "nyse-ccl" → "NYSE.CCL"), so the operator sees them without scrolling.
  const aiSuggestedHoldings = useMemo(() => {
    const shares = aiSuggestion?.shares
    if (!shares) return []
    return Object.entries(shares)
      .filter(([, qty]) => Number(qty) > 0)
      .map(([code, qty]) => {
        const security = securitiesList.find((s: any) => s.code === code)
        return {
          label: security
            ? buildSecurityLabel(security, false)
            : code.toUpperCase(),
          quantity: Number(qty),
        }
      })
  }, [aiSuggestion, securitiesList])

  // Keyboard shortcuts to speed up the review queue:
  //   j / k        next / previous proof of ownership
  //   Enter        start validation (CREATED) / save portfolio + validate (VALIDATING; ⌘/Ctrl+Enter works while typing)
  //   a            accept the AI suggestion (when one is shown)
  //   d            discard
  //   i            open the invalidate modal
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null
      const isTyping =
        !!target &&
        (target.tagName === "INPUT" ||
          target.tagName === "TEXTAREA" ||
          target.tagName === "SELECT" ||
          target.isContentEditable)

      // An open modal owns the keyboard: Enter confirms/submits, and the queue
      // shortcuts below stay suppressed. (Esc-to-cancel is handled by the modal
      // itself via react-modal's onRequestClose.)
      if (confirmModal) {
        if (e.key === "Enter" && !isConfirming) {
          e.preventDefault()
          runConfirmedAction()
        }
        return
      }
      // The reject modal owns its own keyboard (reason selection + submit).
      if (isInvalidationModalOpened) return

      // Save + validate. ⌘/Ctrl+Enter is allowed even while typing in a field.
      if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
        if (isValidating && canFinalize && !isSaving) {
          e.preventDefault()
          handleValidation()
        }
        return
      }

      // Single-key shortcuts must never hijack form input.
      if (isTyping || !hasSelection) return

      switch (e.key) {
        case "j":
        case "k": {
          if (!displayList.length) return
          e.preventDefault()
          const idx = displayList.findIndex(
            item => item.id === selectedStatement.id,
          )
          const next = displayList[idx + (e.key === "j" ? 1 : -1)]
          if (next) setSelectedStatement(next)
          break
        }
        case "a":
          if (
            isValidating &&
            canFinalize &&
            aiSuggestionActionable &&
            !isProcessingAiSuggestion &&
            !isSuggestionDirty
          ) {
            e.preventDefault()
            handleAcceptAiSuggestion()
          }
          break
        case "Enter":
          if (isValidating && canFinalize && !isSaving) {
            e.preventDefault()
            handleValidation()
          } else if (isCreated) {
            e.preventDefault()
            startValidation()
          }
          break
        case "d":
          if (isValidating && canFinalize && !isSaving) {
            e.preventDefault()
            handleDiscard()
          }
          break
        case "i":
          if (isValidating && canFinalize && !isSaving) {
            e.preventDefault()
            setIsInvalidationModalOpened(true)
          }
          break
        case "r":
          if (isValidating && canRelease && !isSaving) {
            e.preventDefault()
            handleRelease()
          }
          break
      }
    }

    window.addEventListener("keydown", handleKeyDown)
    return () => window.removeEventListener("keydown", handleKeyDown)
  }, [
    isValidating,
    isCreated,
    isInvalidationModalOpened,
    confirmModal,
    isConfirming,
    isSaving,
    hasSelection,
    displayList,
    selectedStatement.id,
    aiSuggestionActionable,
    isProcessingAiSuggestion,
    canRelease,
    canFinalize,
    isSuggestionDirty,
    handleValidation,
    handleAcceptAiSuggestion,
    handleDiscard,
    handleRelease,
    startValidation,
    runConfirmedAction,
  ])

  // Update the documented shortcut list (j/k, Enter, a, d, i, r) above as
  // shortcuts change.

  return (
    <PageWrapper>
      {!!user?.email && (
        <InvalidatePooModal
          userEmail={user.email}
          onChangeSelectedItem={setReason}
          onCloseModal={() => setIsInvalidationModalOpened(false)}
          onSubmit={handleSubmitModal}
          isLoading={isSaving}
          isOpen={isInvalidationModalOpened}
          suggestedReason={aiSuggestion?.invalidation_reason}
        />
      )}

      <ConfirmationModal
        isOpen={!!confirmModal}
        title={confirmModal?.title}
        message={confirmModal?.message || ""}
        confirmLabel={confirmModal?.confirmLabel}
        isLoading={isConfirming}
        shortcutHint
        onConfirm={runConfirmedAction}
        onClose={() => {
          if (!isConfirming) setConfirmModal(null)
        }}
      />

      <ProofOfOwnershipHeader
        isFetchingPoo={effectiveLoading}
        isErrorFetchingPooList={isErrorFetchingPooList}
        selectedStatement={selectedStatement}
        setSelectedStatement={setSelectedStatement}
        pooList={effectiveList}
        onSearch={onSearch}
        searchEmail={searchOption.email || null}
        pooFilterStatus={pooFilterStatus}
        setPooFilterStatus={setPooFilterStatus}
        pooCounts={pooCounts}
        isFetchingPooCounts={isFetchingPooCounts}
        assignedCount={ownAssignedQueue.data.length}
        assignedHasMore={ownAssignedQueue.hasNextPage}
        sort={sort}
        setSort={handleSortChange}
        startValidation={startValidation}
        onLoadMore={fetchNextPage}
        hasMore={hasNextPage}
        isLoadingMore={isFetchingNextPage}
        canTakeOver={canTakeOver}
        onTakeOver={handleTakeOver}
        analyzerVersion={
          // Show the version whenever the data endpoint responded. If the
          // analyzer didn't stamp one (e.g. a doc it refused to process), fall
          // back to "0.0.0" so the header still signals "a suggestion came back".
          aiSuggestion
            ? aiSuggestion.analyzer_version?.trim() || "0.0.0"
            : undefined
        }
        versionExtra={
          isValidating &&
          !isFetchingUser &&
          !isLoadingAiSuggestion &&
          aiHint && (
            <AISuggestionNote
              register={form.register}
              watch={form.watch}
              disabled={isForeignValidation}
            />
          )
        }
      />

      {effectiveLoading && <NoTableData>Loading...</NoTableData>}

      {showEmptyState ? (
        <NoTableData>
          {isAssignedView
            ? "No proofs of ownership assigned"
            : isWaitingForUser
              ? `User "${searchOption.email}" not found`
              : searchOption.email
                ? `No proof of ownership for ${searchOption.email} in the ${pooFilterStatus} list`
                : "No Proof of Ownerships."}
        </NoTableData>
      ) : (
        <FormProvider {...form}>
          {isReadOnlyValidation && !isFetchingUser && (
            <ReadOnlyBanner>
              <LockOutlined style={{ fontSize: 20 }} />
              <span>
                Locked — this proof of ownership is being validated by{" "}
                <strong>{selectedAssigneeLabel ?? "another operator"}</strong>.
                All actions are disabled
                {canTakeOver ? " unless you take it over." : "."}
              </span>
            </ReadOnlyBanner>
          )}

          {isValidating &&
            !isFetchingUser &&
            !isLoadingAiSuggestion &&
            aiHint && (
              <AiSuggestionRow>
                {/* We always review the POO by hand; the box only paints a hint
                    to speed that up. not_processed (yellow) means the analyzer
                    never read the document — no data, fill it from scratch;
                    reject (red) and validate (green) carry the AI's suggested
                    disposition. `state` is not branched on — every POO is
                    reviewed here regardless.
                    Read-only for a foreign POO: the data shows, but the disabled
                    fieldset blocks "Accept & Apply". */}
                <ReadOnlyFieldset disabled={isForeignValidation}>
                  {aiHint.kind === "not_processed" ? (
                    <AINotProcessedBox reviewReason={aiHint.reviewReason} />
                  ) : aiHint.kind === "reject" ? (
                    <AISuggestionBox
                      invalidationReason={aiHint.invalidationReason}
                      onAcceptSuggestion={handleAcceptAiSuggestion}
                      isProcessing={isProcessingAiSuggestion}
                      disabled={isSuggestionDirty}
                    />
                  ) : aiHint.kind === "needs_input" ? (
                    // A required field is missing — show the yellow note only.
                    // No green Accept & Apply box: the prefilled fields stay
                    // (amber-highlighted) and the operator enters the missing
                    // value before finalizing through the normal save flow.
                    <AIReviewReasonNote reviewReason={aiHint.reviewReason} />
                  ) : aiHint.kind === "confirm_authenticity" ? (
                    // The document itself is flagged as possibly not genuine —
                    // one combined amber box (warning + Accept & Apply All),
                    // not a separate yellow note above a green box.
                    <AIAuthenticityConfirmBox
                      reviewReason={aiHint.reviewReason}
                      onAcceptSuggestion={handleAcceptAiSuggestion}
                      isProcessing={isProcessingAiSuggestion}
                      disabled={isSuggestionDirty}
                      holdings={aiSuggestedHoldings}
                    />
                  ) : (
                    <AIValidationSuggestionBox
                      onAcceptSuggestion={handleAcceptAiSuggestion}
                      isProcessing={isProcessingAiSuggestion}
                      disabled={isSuggestionDirty}
                      holdings={aiSuggestedHoldings}
                      reviewReason={aiHint.reviewReason}
                    />
                  )}
                </ReadOnlyFieldset>
              </AiSuggestionRow>
            )}

          {showContentArea && (
            <ContentRow>
              <ProofOfOwnershipViewer
                isLoadingList={isFetchingPoo}
                item={selectedStatement}
              />

              {showForm && (
                <FormPanel>
                  <ReadOnlyFieldset disabled={isForeignValidation}>
                    <Portfolio
                      userId={getPooUserId(selectedStatement)}
                      userName={
                        aiSuggestion?.person_name || selectedStatement.name
                      }
                      userEmail={user.email}
                      brokerName={
                        aiSuggestion?.brokerage_name || selectedStatement.broker
                      }
                      month={
                        aiSuggestion?.document_date
                          ? moment(aiSuggestion.document_date).month() + 1
                          : moment().month() + 1
                      }
                      year={
                        aiSuggestion?.document_date
                          ? moment(aiSuggestion.document_date).year()
                          : moment().year()
                      }
                      showPooFields={true}
                      showSubmitButton={false}
                      form={form}
                      isLoading={false}
                      aiEnhancedPortfolio={aiEnhancedPortfolio}
                      aiFilledFields={aiFilledFields}
                      aiSuggestsReject={aiSuggestsReject}
                      aiSuggestsReview={aiSuggestsReview}
                      securities={securitiesList}
                      stackLabel
                    />
                  </ReadOnlyFieldset>
                </FormPanel>
              )}
            </ContentRow>
          )}

          {isValidating &&
            canFinalize &&
            !isFetchingUser &&
            !isLoadingAiSuggestion && (
              <ValidationActions
                isSaving={isSaving}
                onDiscard={handleDiscard}
                onInvalidate={() => setIsInvalidationModalOpened(true)}
                onValidate={handleValidation}
                onRelease={handleRelease}
                canRelease={canRelease}
              />
            )}
        </FormProvider>
      )}
    </PageWrapper>
  )
}

const PageWrapper = styled(PageContent)`
  display: flex;
  flex-direction: column;
  height: calc(100vh - 90px);
  overflow: hidden;
  padding: 0;
`

// Layout-neutral wrapper that natively disables every form control inside it
// when `disabled` (so a foreign POO's data is visible but not editable).
const ReadOnlyFieldset = styled.fieldset`
  border: none;
  margin: 0;
  padding: 0;
  min-width: 0;

  &[disabled] {
    opacity: 0.7;
    pointer-events: none;
  }
`

const AiSuggestionRow = styled.div`
  flex-shrink: 0;
  padding: 0 12px;
  margin-bottom: 10px;
`

const ReadOnlyBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
  margin: 0 12px 10px;
  padding: 12px 16px;
  border: 1px solid #f0b429;
  border-radius: 6px;
  background: #fff7e6;
  color: #8a5a00;
  font-size: 14px;
  font-weight: 500;

  strong {
    color: #6b4500;
    font-weight: 700;
  }
`

const ContentRow = styled.div`
  display: flex;
  flex: 1;
  min-height: 0;
  gap: 10px;
  padding: 0 12px 12px;
`

const FormPanel = styled.div`
  width: 460px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  border: 1px solid ${colors.gray2};
  border-radius: 6px;
  background: ${colors.white};
  overflow-y: auto;
  padding: 16px;
`

export default CreatePortfolio
